/**
 * Header Component
 * 
 * A modern, responsive header that displays the site logo, navigation menu,
 * language switcher, and theme toggle. Features a mobile-friendly design with
 * a hamburger menu for smaller screens.
 * 
 * Features:
 * - Responsive design with mobile hamburger menu
 * - Smooth animations and transitions
 * - i18n support for navigation links
 * - Integrated language switcher and theme toggle
 * - Sticky positioning with backdrop blur effect
 */

import React, { useState, useEffect } from "react";
import Link from "next/link";
import Image from "next/image";
import { useTranslation } from 'next-i18next';
import { useRouter } from "next/router";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from 'lucide-react';

import LanguageSwitcher from "../common/LanguageSwitcher";
import ThemeToggle from "../common/ThemeToggle";

/**
 * Navigation links structure
 * - For "guide" and "faq", use section scroll on home page.
 */
const NAV_LINKS = [
  { href: "/", key: "home" },
  { href: "#howto", key: "guide", sectionId: "howto" },
  { href: "#faq", key: "faq", sectionId: "faq" },
  { href: "/contact", key: "contact" },
];

const Header: React.FC = () => {
  const { t } = useTranslation('header');
  const router = useRouter();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  
  // Handle scroll effect for sticky header
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  
  // Toggle mobile menu
  const toggleMenu = () => setIsMenuOpen(!isMenuOpen);
  
  // Close menu when clicking a link (mobile)
  const closeMenu = () => setIsMenuOpen(false);

  // Helper: Check if current page is home (with locale)
  const isHome = typeof window !== "undefined" &&
    (router.asPath === "/" || router.asPath === `/${router.locale}` || router.asPath === `/${router.locale}/`);

  // Scroll to section by id
  const scrollToSection = (id: string) => {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: "smooth" });
    }
  };

  // Handle menu click for section links
  const handleMenuClick = (e: React.MouseEvent, sectionId?: string, href?: string) => {
    if (sectionId) {
      e.preventDefault();
      if (isHome) {
        scrollToSection(sectionId);
      } else {
        // Build path with locale prefix
        const localePrefix = router.locale ? `/${router.locale}` : "";
        // Remove leading slash from href if present
        const hash = href && href.startsWith("#") ? href : `#${sectionId}`;
        router.push(`${localePrefix}/${hash}`.replace(/\/+/, "/"));
      }
      setIsMenuOpen(false);
    }
  };

  // On mount: if hash present and on home, scroll to section
  useEffect(() => {
    if (typeof window !== "undefined" && window.location.hash) {
      const id = window.location.hash.replace("#", "");
      setTimeout(() => scrollToSection(id), 100); // delay for DOM ready
    }
  }, []);

  return (
    <header className={`sticky top-0 z-50 w-full transition-all duration-300 ${
      isScrolled ? 'bg-white/80 dark:bg-gray-900/80 backdrop-blur-md shadow-md' : 'bg-white dark:bg-gray-900'
    }`}>
      <div className="container mx-auto flex items-center justify-between py-4 px-4">
        {/* Logo and site name */}
        <motion.div 
          className="flex items-center space-x-2"
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5 }}
        >
          <Link href="/" className="flex items-center">
            <div className="relative w-[120px] h-12 overflow-hidden">
              {/* @ts-ignore */}
              <Image
                src="/downloadmod_logo.png"
                alt="downloadmod.com"
                fill
                className="object-contain w-full h-full"
                sizes="120px"
                priority
              />
            </div>
            {/* Fallback text nếu muốn, có thể thêm onError cho Image để hiện text nếu ảnh lỗi */}
          </Link>
        </motion.div>

        {/* Desktop Navigation menu */}
        <nav className="hidden md:flex space-x-8">
          {NAV_LINKS.map((link, index) => (
            <motion.div
              key={link.href}
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: 0.1 * index }}
            >
              {link.sectionId ? (
                <a
                  href={link.href}
                  className="text-gray-700 dark:text-gray-200 hover:text-primary dark:hover:text-primary font-medium transition-colors relative group cursor-pointer"
                  onClick={e => handleMenuClick(e, link.sectionId, link.href)}
                >
                  {t(link.key)}
                  <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-primary transition-all duration-300 group-hover:w-full"></span>
                </a>
              ) : (
                <Link
                  href={link.href}
                  className="text-gray-700 dark:text-gray-200 hover:text-primary dark:hover:text-primary font-medium transition-colors relative group"
                >
                  {t(link.key)}
                  <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-primary transition-all duration-300 group-hover:w-full"></span>
                </Link>
              )}
            </motion.div>
          ))}
        </nav>

        {/* Language switcher & theme toggle */}
        <motion.div 
          className="flex items-center space-x-2"
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5 }}
        >
          <LanguageSwitcher />
          <ThemeToggle />
          
          {/* Mobile menu button */}
          <button 
            className="ml-2 p-2 rounded-full bg-gray-100 dark:bg-gray-800 md:hidden focus-ring"
            onClick={toggleMenu}
            aria-label={isMenuOpen ? "Close menu" : "Open menu"}
            aria-expanded={isMenuOpen}
          >
            {isMenuOpen ? (
              <X className="w-5 h-5 text-gray-700 dark:text-gray-200" />
            ) : (
              <Menu className="w-5 h-5 text-gray-700 dark:text-gray-200" />
            )}
          </button>
        </motion.div>
      </div>
      
      {/* Mobile Navigation Menu */}
      <AnimatePresence>
        {isMenuOpen && (
          <motion.div
            className="md:hidden"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
          >
            <nav className="flex flex-col space-y-4 px-4 py-6 bg-white dark:bg-gray-900 border-t border-gray-100 dark:border-gray-800">
              {NAV_LINKS.map((link, index) => (
                <motion.div
                  key={link.href}
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.3, delay: 0.05 * index }}
                >
                  {link.sectionId ? (
                    <a
                      href={link.href}
                      className="block text-gray-700 dark:text-gray-200 hover:text-primary dark:hover:text-primary font-medium py-2 cursor-pointer"
                      onClick={e => handleMenuClick(e, link.sectionId, link.href)}
                    >
                      {t(link.key)}
                    </a>
                  ) : (
                    <Link
                      href={link.href}
                      className="block text-gray-700 dark:text-gray-200 hover:text-primary dark:hover:text-primary font-medium py-2"
                      onClick={closeMenu}
                    >
                      {t(link.key)}
                    </Link>
                  )}
                </motion.div>
              ))}
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

// Export as default
export default Header;
